import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Button, Chip, Stack, TextField, Typography, Checkbox, FormControlLabel,
  Dialog, DialogActions, DialogContent, DialogTitle,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { RowAction } from '../../components/data-grid/RouteDataGrid';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import {
  useUserPubKeys,
  useAddUserPubKey,
  useRevokeUserPubKey,
  type UserPubKeyDto,
} from '../../api/queries';
import { useConfirm } from '../../components/dialogs/DialogProvider';

/**
 * S2 — minimal admin surface for system-user public keys, per the Service
 * Auth methodology. ACES gates: auth.system-users.admin.
 *
 * Route: /admin/users/:userId/keys
 *
 * Operator workflow:
 *   1. Generate a keypair locally:
 *        bash hardening-pipeline/generate-system-keypair.sh xu4-pipeline-prod
 *   2. Copy the contents of <name>.pub.b64url
 *   3. Paste here, click "Register key"
 *   4. Copy the resulting CI secrets to the workflow / build host:
 *        RMS_SYSTEM_USERNAME / RMS_SYSTEM_PASSWORD / RMS_SYSTEM_PRIVATE_KEY_FILE
 */
export default function SystemUserKeysPage() {
  usePageTitle('System user keys');
  const { userId = '' } = useParams<{ userId: string }>();
  const { queryKey, listFn } = useUserPubKeys(userId);

  const addMut = useAddUserPubKey();
  const revokeMut = useRevokeUserPubKey();
  const confirm = useConfirm();

  const [addOpen, setAddOpen] = useState(false);
  const [pubKey, setPubKey] = useState('');
  const [keyName, setKeyName] = useState('');
  const [revokeExisting, setRevokeExisting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitAdd = async () => {
    setError(null);
    try {
      await addMut.mutateAsync({
        userId,
        publicKeyBase64Url: pubKey.trim(),
        displayName: keyName.trim() || undefined,
        revokeExisting,
      });
      setAddOpen(false);
      setPubKey('');
      setKeyName('');
      setRevokeExisting(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const cols: GridColDef<UserPubKeyDto>[] = [
    { field: 'displayName', headerName: 'Name', width: 240 },
    { field: 'algorithm', headerName: 'Algorithm', width: 100 },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 170,
      renderCell: ({ value }) => fmtDateTime(value as string),
    },
    {
      field: 'lastUsedAt',
      headerName: 'Last used',
      width: 170,
      renderCell: ({ value }) => fmtOrEmDash(value as string | null, fmtDateTime),
    },
    {
      field: 'revokedAt',
      headerName: 'State',
      width: 110,
      renderCell: ({ value }) => (
        <Chip
          label={value ? 'revoked' : 'active'}
          size="small"
          color={value ? 'default' : 'success'}
          variant={value ? 'outlined' : 'filled'}
        />
      ),
    },
  ];

  const exportColumns: ExportColumn<UserPubKeyDto>[] = [
    { header: 'Name', value: r => r.displayName },
    { header: 'Algorithm', value: r => r.algorithm },
    { header: 'Created', value: r => r.createdAt },
    { header: 'Last used', value: r => r.lastUsedAt ?? '—' },
    { header: 'State', value: r => r.revokedAt ? 'revoked' : 'active' },
  ];

  const actions: RowAction<UserPubKeyDto>[] = [
    {
      label: 'Revoke',
      destructive: true,
      visible: row => !row.revokedAt,
      onClick: async row => {
        const ok = await confirm({
          title: 'Revoke API key',
          message: <>Revoke key <strong>{row.displayName}</strong>? This is immediate and irreversible — any client still using it will get a 401 on the next call.</>,
          confirmLabel: 'Revoke',
          destructive: true,
        });
        if (!ok) return;
        await revokeMut.mutateAsync({ userId, keyId: row.id });
      },
    },
  ];

  return (
    <>
      <Stack spacing={2} sx={{ p: 2 }}>
        <Typography variant="h5">System user public keys</Typography>
        <Typography variant="body2" color="text.secondary">
          Per-user Ed25519 keys for the system-auth flow (challenge-response). Exactly one
          active key per user; rotation = revoke + add new (atomic when "Replace existing" is
          checked).
        </Typography>

        <Box>
          <Button variant="contained" onClick={() => setAddOpen(true)}>
            Register new key
          </Button>
        </Box>

        <RouteDataGrid<UserPubKeyDto>
          entityName="Key"
          queryKey={queryKey}
          listFn={listFn}
          columns={cols}
          exportColumns={exportColumns}
          actions={actions}
          reportTitle={`System user pubkeys`}
        />
      </Stack>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Register public key</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Paste the contents of <code>&lt;name&gt;.pub.b64url</code> produced by{' '}
              <code>hardening-pipeline/generate-system-keypair.sh</code>. Raw 32-byte
              Ed25519 public key, base64url-encoded.
            </Typography>
            <TextField
              label="Public key (base64url)"
              value={pubKey}
              onChange={e => setPubKey(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              autoFocus
              spellCheck={false}
              InputProps={{ sx: { fontFamily: 'Menlo,Consolas,monospace', fontSize: 12 } }}
            />
            <TextField
              label="Friendly name"
              value={keyName}
              onChange={e => setKeyName(e.target.value)}
              fullWidth
              placeholder="e.g. xu4-pipeline-prod"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={revokeExisting}
                  onChange={e => setRevokeExisting(e.target.checked)}
                />
              }
              label="Atomic rotation: revoke any existing active key for this user"
            />
            {error && <Typography color="error">{error}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitAdd} disabled={!pubKey.trim()}>
            Register
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
